<template>
  <div class="thoughts-page">
    <!-- 左侧：新增任务 -->
    <div class="left">
      <div class="title">
        <div class="icon">✎</div>
        记录随想
      </div>

      <div v-if="!isLoggedIn()" class="offline-tip">未登录，随想只存在这台设备上，登录后会自动同步</div>

      <div class="add-card">
        <div class="add-row">
          <input
            v-model="draft"
            type="text"
            name="thought-new-content"
            autocomplete="off"
            class="add-input"
            placeholder="想到什么，先记下来…"
            maxlength="80"
            @keydown.enter="onPost"
          >
          <button class="add-btn" :disabled="!draft.trim()" @click="onPost">记下来</button>
        </div>
        <div class="add-meta">
          <div class="meta-group">
            <span class="meta-label">分类</span>
            <div class="chip-row">
              <button
                v-for="cat in categories"
                :key="cat"
                class="chip"
                :class="[catClass(cat), { active: category === cat }]"
                @click="category = cat"
              >{{ cat }}</button>
            </div>
          </div>
          <div class="meta-group">
            <span class="meta-label">优先级</span>
            <div class="chip-row">
              <button
                v-for="pri in priorities"
                :key="pri"
                class="chip"
                :class="[priClass(pri), { active: priority === pri }]"
                @click="priority = pri"
              >{{ pri }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧：任务列表 + 筛选 -->
    <div class="right">
      <div class="title">
        <div class="icon">☰</div>
        随想列表
      </div>

      <div class="stats-line">
        <span>共 <b>{{ counts['全部'] }}</b> 条</span>
        <span class="dot">·</span>
        <span>待办 <b>{{ counts['未完成'] }}</b></span>
        <span class="dot">·</span>
        <span :class="{ warn: counts['逾期'] > 0 }">逾期 <b>{{ counts['逾期'] }}</b></span>
        <span class="dot">·</span>
        <span>已完成 <b>{{ counts['已完成'] }}</b></span>
      </div>

      <div class="search-row">
        <input
          v-model="keyword"
          type="text"
          name="thought-search"
          autocomplete="off"
          class="search-input"
          placeholder="搜一下记过什么…"
        >
        <button v-if="keyword" class="search-clear" title="清空搜索" @click="keyword = ''">×</button>
      </div>

      <div class="tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="tab"
          :class="{ active: filter === tab.key }"
          @click="filter = tab.key"
        >
          {{ tab.label }} <span class="count">{{ counts[tab.key] }}</span>
        </button>
        <button
          class="tab sort-toggle"
          :class="{ active: sortBy === 'priority' }"
          :title="sortBy === 'priority' ? '当前按优先级排，点回按时间排' : '当前按时间排，点一下按优先级排'"
          @click="toggleSort"
        >{{ sortBy === 'priority' ? '按优先级排' : '按时间排' }}</button>
        <button
          v-if="filter === '已完成' && counts['已完成'] > 0"
          class="tab clear-btn"
          :disabled="clearing"
          title="一次性清掉所有已完成的随想"
          @click="clearCompleted"
        >{{ clearing ? '清理中…' : '清空已完成' }}</button>
      </div>

      <div class="grid">
        <div
          v-for="item in filteredItems"
          :key="item.id"
          class="card"
          :class="{ done: item.status === '已完成' }"
          :style="{ transform: `rotate(${rotation(item.id)}deg)` }"
        >
          <div class="card-top">
            <div class="checkbox" :class="{ checked: item.status === '已完成' }" @click="toggleDone(item.id)">
              {{ item.status === '已完成' ? '✓' : '' }}
            </div>
            <div class="card-text">{{ item.content }}</div>
          </div>
          <div v-if="item.detail" class="card-detail">
            <div class="detail-text" :class="{ expanded: isExpanded(item.id) }">{{ item.detail }}</div>
            <button class="detail-toggle" @click="toggleExpand(item.id)">
              {{ isExpanded(item.id) ? '收起' : '展开' }}
            </button>
          </div>
          <div class="tag-row">
            <span class="tag" :class="catClass(item.type)">{{ item.type }}</span>
            <span class="tag" :class="priClass(item.priority)">优先级 · {{ item.priority }}</span>
          </div>
          <div
            v-if="item.plan && item.date"
            class="schedule-line"
            :class="{ 'is-overdue': isOverdue(item), 'is-today': isToday(item) }"
          >
            <span v-if="isOverdue(item)" class="badge badge-overdue">已逾期</span>
            <span v-else-if="isToday(item)" class="badge badge-today">今天</span>
            🗓 {{ item.date }} {{ item.start || '' }}{{ item.end ? ' - ' + item.end : '' }}
          </div>
          <div class="card-actions">
            <button
              class="action-btn convert-btn"
              :disabled="!isLoggedIn()"
              :title="isLoggedIn() ? '把这条随想变成时间表里的一个时间块' : '登录后才能转为时间块'"
              @click="openConvert(item.id)"
            >转为时间块</button>
            <div class="action-row">
              <button class="action-btn" @click="openEdit(item.id)">编辑</button>
              <button class="action-btn plan-btn" @click="openSchedule(item.id)">安排时间</button>
              <button class="action-btn del" @click="openDelete(item.id)">删除</button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="empty">
          <div>正在加载…</div>
        </div>
        <div v-else-if="loadError" class="empty">
          <div class="em-face">加载失败了</div>
          <div>{{ loadError }}</div>
          <button class="action-btn" @click="loadFromServer">重试</button>
        </div>
        <div v-else-if="filteredItems.length === 0" class="empty">
          <div class="em-face">这里空空如也～</div>
          <div>写点什么，把想法留住</div>
        </div>
      </div>
    </div>

    <!-- 安排时间弹窗 -->
    <div class="overlay" :class="{ open: scheduleTarget !== null }" @click.self="scheduleTarget = null">
      <div class="modal">
        <h3>安排任务</h3>
        <div class="field">
          <label>日期</label>
          <input v-model="scheduleForm.date" type="date">
        </div>
        <div class="field">
          <label>时间</label>
          <div class="time-row">
            <input v-model="scheduleForm.start" type="time">
            <span>至</span>
            <input v-model="scheduleForm.end" type="time">
          </div>
        </div>
        <div class="field">
          <label>分类</label>
          <select v-model="scheduleForm.type">
            <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
          </select>
        </div>
        <div class="modal-actions">
          <button class="cancel" @click="scheduleTarget = null">取消</button>
          <button class="save" @click="saveSchedule">保存</button>
        </div>
      </div>
    </div>

    <!-- 转为时间块弹窗 -->
    <div class="overlay" :class="{ open: convertTarget !== null }" @click.self="convertTarget = null">
      <div class="modal">
        <h3>转为时间块</h3>
        <p class="modal-tip">转换后这条随想会出箱，按下面的时间落进对应工作表的时间表。</p>
        <div class="field">
          <label>工作表</label>
          <select v-model="convertForm.projectId" :disabled="convertLoading" @change="loadConvertTags">
            <option v-for="p in convertProjects" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>
        <div class="field">
          <label>分类（可选）</label>
          <select v-model="convertForm.tagId" :disabled="convertLoading">
            <option value="">不分类</option>
            <option v-for="t in convertTags" :key="t.id" :value="String(t.id)">{{ tagName(t) }}</option>
          </select>
        </div>
        <div class="field">
          <label>日期</label>
          <input v-model="convertForm.date" type="date">
        </div>
        <div class="field">
          <label>时间</label>
          <div class="time-row">
            <input v-model="convertForm.start" type="time">
            <span>至</span>
            <input v-model="convertForm.end" type="time">
          </div>
        </div>
        <div class="modal-actions">
          <button class="cancel" @click="convertTarget = null">取消</button>
          <button class="save" :disabled="convertSaving || convertLoading" @click="confirmConvert">
            {{ convertSaving ? '转换中…' : '转换' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 编辑随想弹窗 -->
    <div class="overlay" :class="{ open: editTarget !== null }" @click.self="editTarget = null">
      <div class="modal">
        <h3>编辑随想</h3>
        <div class="field">
          <label>内容</label>
          <input
            v-model="editForm.content"
            type="text"
            name="thought-edit-content"
            autocomplete="off"
            maxlength="80"
            placeholder="写下这条随想…"
            @keydown.enter="saveEdit"
          >
        </div>
        <div class="field">
          <label>补充说明（可选）</label>
          <textarea
            v-model="editForm.detail"
            name="thought-edit-detail"
            class="detail-input"
            rows="4"
            maxlength="2000"
            placeholder="想展开写点什么…"
          ></textarea>
        </div>
        <div class="field">
          <label>分类</label>
          <select v-model="editForm.type">
            <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
          </select>
        </div>
        <div class="field">
          <label>优先级</label>
          <select v-model="editForm.priority">
            <option v-for="pri in priorities" :key="pri" :value="pri">{{ pri }}</option>
          </select>
        </div>
        <div class="modal-actions">
          <button class="cancel" @click="editTarget = null">取消</button>
          <button class="save" :disabled="!editForm.content.trim()" @click="saveEdit">保存</button>
        </div>
      </div>
    </div>

    <!-- 删除确认弹窗：删除不可恢复，必须先确认一次 -->
    <div class="overlay" :class="{ open: deleteTarget !== null }" @click.self="deleteTarget = null">
      <div class="modal">
        <h3>删除随想</h3>
        <p class="modal-tip">删除后不可恢复，确定要删掉这条吗？</p>
        <div v-if="pendingDelete" class="confirm-quote">{{ pendingDelete.content }}</div>
        <div class="modal-actions">
          <button class="cancel" @click="deleteTarget = null">取消</button>
          <button class="save danger" @click="confirmDelete">删除</button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div class="toast" :class="{ show: toastMsg }">{{ toastMsg }}</div>
  </div>
</template>

<script>
import { listThoughts, createThought, updateThought, scheduleThought, completeThought, deleteThought, convertThought, batchDeleteThoughts } from '@/api/thought.js'
import { listWorkspaces } from '@/api/workspace.js'
import { listProjects } from '@/api/project.js'
import { listTagsByProject } from '@/api/tag.js'

const STORAGE_KEY = 'taskblock-thoughts'
const MIGRATED_KEY = 'taskblock-thoughts-migrated'

// 历史版本内置的三条「示例数据」，现已在代码中移除（新用户不会再看到）。
// 但老用户浏览器 localStorage 里可能还留着它们，首次登录时会被整包上传到数据库，
// 造成脏数据。这里保留内容指纹，用于本地清理与迁移时过滤。
const SAMPLE_CONTENTS = [
  '完成随想页面原型',
  '买一束向日葵',
  '读完《人类简史》第三章'
]

const CATEGORIES = ['工作', '生活', '学习', '其他']
const PRIORITIES = ['高', '中', '低']

const CATEGORY_CODE = { '工作': 'WORK', '生活': 'LIFE', '学习': 'STUDY', '其他': 'OTHER' }
const CATEGORY_CN = { WORK: '工作', LIFE: '生活', STUDY: '学习', OTHER: '其他' }
const PRIORITY_CODE = { '高': 'HIGH', '中': 'MEDIUM', '低': 'LOW' }
const PRIORITY_CN = { HIGH: '高', MEDIUM: '中', LOW: '低' }
const STATUS_CN = { PENDING: '未完成', SCHEDULED: '进行中', DONE: '已完成' }

const ROTATIONS = [-2.5, -1, 1, 2, -1.8, 1.6, 0.5, -0.8]

// 优先级权重，用于「按优先级排」：高 > 中 > 低
const PRIORITY_WEIGHT = { '高': 3, '中': 2, '低': 1 }

export default {
  name: 'ThoughtsPage',
  data() {
    return {
      items: [],
      draft: '',
      category: '工作',
      priority: '中',
      filter: '全部',
      sortBy: 'new',
      keyword: '',
      // 删除前先弹确认：删除不可恢复，不能一点就没
      deleteTarget: null,
      clearing: false,
      scheduleTarget: null,
      scheduleForm: { date: '', start: '09:00', end: '10:00', type: '工作' },
      // 「编辑随想」弹窗状态
      editTarget: null,
      editForm: { content: '', detail: '', type: '工作', priority: '中' },
      // 长备注展开状态：id -> bool（新增 key 一律用 $set）
      expanded: {},
      // 「转为时间块」弹窗状态
      convertTarget: null,
      convertForm: { projectId: '', tagId: '', date: '', start: '09:00', end: '10:00' },
      convertProjects: [],
      convertTags: [],
      convertSaving: false,
      convertLoading: false,
      workspaceId: '',
      toastMsg: '',
      toastTimer: null,
      loading: false,
      loadError: '',
      busy: false
    }
  },
  computed: {
    categories() {
      return CATEGORIES
    },
    priorities() {
      return PRIORITIES
    },
    tabs() {
      return [
        { key: '全部', label: '全部' },
        { key: '未完成', label: '未完成' },
        { key: '已安排', label: '已安排' },
        { key: '今天', label: '今天' },
        { key: '逾期', label: '逾期' },
        { key: '高优先级', label: '高优先级' },
        { key: '已完成', label: '已完成' }
      ]
    },
    counts() {
      return {
        '全部': this.items.length,
        '未完成': this.items.filter(t => t.status !== '已完成').length,
        '已安排': this.items.filter(t => t.plan === true).length,
        '今天': this.items.filter(t => this.isToday(t)).length,
        '逾期': this.items.filter(t => this.isOverdue(t)).length,
        '高优先级': this.items.filter(t => t.priority === '高' && t.status !== '已完成').length,
        '已完成': this.items.filter(t => t.status === '已完成').length
      }
    },
    // 排序：默认沿用后端顺序（创建时间倒序，新记的排最前），切到「优先级」后本地重排。
    // 随想条目量级小（几百条内），本地排序零延迟，不必为此给后端加排序参数。
    sortedItems() {
      if (this.sortBy !== 'priority') return this.items
      return this.items.slice().sort((a, b) =>
        (PRIORITY_WEIGHT[b.priority] || 2) - (PRIORITY_WEIGHT[a.priority] || 2))
    },
    pendingDelete() {
      return this.items.find(x => x.id === this.deleteTarget) || null
    },
    filteredItems() {
      const kw = this.keyword.trim().toLowerCase()
      let list = this.sortedItems
      if (kw) {
        list = list.filter(t => (t.content || '').toLowerCase().indexOf(kw) !== -1)
      }
      switch (this.filter) {
        case '未完成': return list.filter(t => t.status !== '已完成')
        case '已完成': return list.filter(t => t.status === '已完成')
        case '已安排': return list.filter(t => t.plan === true)
        case '今天': return list.filter(t => this.isToday(t))
        case '逾期': return list.filter(t => this.isOverdue(t))
        case '高优先级': return list.filter(t => t.priority === '高' && t.status !== '已完成')
        default: return list
      }
    }
  },
  created() {
    // 先用本地缓存渲染一屏（断网/慢网时不至于白屏），再回源覆盖
    this.loadLocal()
    if (this.isLoggedIn()) {
      this.loadFromServer()
    }
  },
  methods: {
    isLoggedIn() {
      return !!localStorage.getItem('token')
    },
    // 历史内置示例数据的指纹判断：命中则视为脏数据，不展示、不上传
    isSample(d) {
      return !!d && SAMPLE_CONTENTS.indexOf(String(d.content || '').trim()) !== -1
    },
    loadLocal() {
      let stored = null
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) stored = parsed
        }
      } catch (e) { /* ignore */ }

      // 本机没有随想就是空的，不再塞示例数据
      if (!stored || stored.length === 0) {
        this.items = []
        return
      }

      // 老格式（只有 content）补齐字段
      let list = stored
      const converted = stored[0].status === undefined
      if (converted) {
        list = stored.map((old, idx) => ({
          id: old.id || (idx + 1),
          content: old.content || '',
          type: '其他',
          priority: '中',
          status: '未完成',
          plan: false,
          createTime: Date.now()
        }))
      }

      // 清掉历史示例数据，并把清理结果写回本机，避免下次登录又被上传
      const cleaned = list.filter(d => !this.isSample(d))
      this.items = cleaned
      if (converted || cleaned.length !== stored.length) {
        this.persist()
      }
    },
    async loadFromServer() {
      this.loading = true
      this.loadError = ''
      try {
        const res = await listThoughts()
        if (res && (res.success || res.code === 200)) {
          this.items = (res.data || []).map(vo => this.fromVO(vo))
          // 迁移要在写缓存之前：迁移读的就是 localStorage 里的本机草稿
          await this.migrateLocalThoughts()
          // 登录后也缓存一份，下次进页面先渲染缓存、断网也看得到内容
          this.persist()
        } else {
          this.loadError = (res && res.message) || '加载失败'
        }
      } catch (e) {
        this.loadError = '网络异常，稍后重试'
      } finally {
        this.loading = false
      }
    },
    // 首次登录时把本机草稿搬到服务器，只搬一次
    async migrateLocalThoughts() {
      if (localStorage.getItem(MIGRATED_KEY)) return
      let drafts = []
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
          const parsed = JSON.parse(raw)
          // 只搬真实草稿：历史内置示例数据一律丢弃，不写进数据库
          if (Array.isArray(parsed)) drafts = parsed.filter(d => !this.isSample(d))
        }
      } catch (e) { /* ignore */ }
      if (drafts.length === 0) {
        localStorage.setItem(MIGRATED_KEY, '1')
        return
      }
      let okCount = 0
      for (const d of drafts) {
        try {
          const res = await createThought({
            content: d.content || '',
            category: CATEGORY_CODE[d.type] || 'OTHER',
            priority: PRIORITY_CODE[d.priority] || 'MEDIUM'
          })
          if (res && (res.success || res.code === 200)) okCount++
        } catch (e) { /* 单条失败不中断整体迁移 */ }
      }
      localStorage.setItem(MIGRATED_KEY, '1')
      if (okCount > 0) {
        const res = await listThoughts()
        if (res && (res.success || res.code === 200)) {
          this.items = (res.data || []).map(vo => this.fromVO(vo))
        }
        this.showToast(`已同步 ${okCount} 条本机随想`)
      }
    },
    // 后端数据 → 页面展示结构
    fromVO(vo) {
      const s = vo.startTime ? String(vo.startTime) : ''
      const e = vo.endTime ? String(vo.endTime) : ''
      return {
        id: vo.id,
        content: vo.content || '',
        detail: vo.detail || '',
        type: CATEGORY_CN[vo.category] || '其他',
        priority: PRIORITY_CN[vo.priority] || '中',
        status: STATUS_CN[vo.status] || '未完成',
        plan: !!vo.startTime,
        createTime: vo.createdAt ? new Date(String(vo.createdAt).replace(' ', 'T')).getTime() : Date.now(),
        date: s ? s.slice(0, 10) : '',
        start: s ? s.slice(11, 16) : '',
        end: e ? e.slice(11, 16) : ''
      }
    },
    // 登录后也要写：这份缓存既是首屏兜底，也是断网时唯一能看到的内容。
    // ⚠️ 调用时机必须在 migrateLocalThoughts() 之后，否则会覆盖掉待迁移的本机草稿。
    persist() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items))
      } catch (e) { /* ignore */ }
    },
    nextId() {
      return this.items.reduce((m, it) => Math.max(m, Number(it.id) || 0), 0) + 1
    },
    fmtDate(d) {
      const pad = n => n.toString().padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    today() {
      return this.fmtDate(new Date())
    },
    nowHm() {
      const d = new Date()
      const pad = n => n.toString().padStart(2, '0')
      return `${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    // 今天安排了、且还没做完
    isToday(t) {
      return t.plan === true && t.date === this.today() && t.status !== '已完成'
    },
    // 安排时间已过、且还没做完：整条日期已过，或就在今天但结束时间已过
    isOverdue(t) {
      if (t.plan !== true || t.status === '已完成' || !t.date) return false
      const today = this.today()
      if (t.date < today) return true
      return t.date === today && !!t.end && t.end < this.nowHm()
    },
    toggleSort() {
      this.sortBy = this.sortBy === 'priority' ? 'new' : 'priority'
    },
    // 雪花 ID 超过 JS 安全整数范围，改用字符串散列取角度
    rotation(id) {
      const str = String(id)
      let n = 0
      for (let i = 0; i < str.length; i++) n = (n * 31 + str.charCodeAt(i)) >>> 0
      return ROTATIONS[n % ROTATIONS.length]
    },
    catClass(type) {
      const map = { '工作': 'work', '生活': 'life', '学习': 'study', '其他': 'other' }
      return map[type] || 'other'
    },
    priClass(priority) {
      const map = { '高': 'hi', '中': 'mid', '低': 'low' }
      return map[priority] || 'mid'
    },
    async onPost() {
      const text = this.draft.trim()
      if (!text || this.busy) return
      if (this.isLoggedIn()) {
        this.busy = true
        try {
          const res = await createThought({
            content: text,
            category: CATEGORY_CODE[this.category] || 'OTHER',
            priority: PRIORITY_CODE[this.priority] || 'MEDIUM'
          })
          if (res && (res.success || res.code === 200)) {
            this.items.unshift(this.fromVO(res.data))
          } else {
            this.showToast((res && res.message) || '保存失败，请重试')
            return
          }
        } catch (e) {
          this.showToast('网络异常，请重试')
          return
        } finally {
          this.busy = false
        }
      } else {
        this.items.unshift({
          id: this.nextId(),
          content: text,
          type: this.category,
          priority: this.priority,
          status: '未完成',
          plan: false,
          createTime: Date.now()
        })
        this.persist()
      }
      this.draft = ''
      this.showToast('已记下这件事 ✍️')
    },
    async toggleDone(id) {
      const t = this.items.find(x => x.id === id)
      if (!t) return
      const next = t.status === '已完成' ? (t.plan ? '进行中' : '未完成') : '已完成'
      if (this.isLoggedIn()) {
        this.busy = true
        try {
          const res = await completeThought(id, next === '已完成')
          if (res && (res.success || res.code === 200)) {
            Object.assign(t, this.fromVO(res.data))
          } else {
            this.showToast((res && res.message) || '操作失败，请重试')
          }
        } catch (e) {
          this.showToast('网络异常，请重试')
        } finally {
          this.busy = false
        }
        return
      }
      t.status = next
      this.persist()
    },
    // 删除走二次确认：软删除没有回收站、也不做撤销，一点就没代价太大
    openDelete(id) {
      this.editTarget = null
      this.scheduleTarget = null
      this.deleteTarget = id
    },
    async confirmDelete() {
      const t = this.pendingDelete
      if (!t) return
      if (this.isLoggedIn()) {
        this.busy = true
        try {
          const res = await deleteThought(t.id)
          if (!(res && (res.success || res.code === 200))) {
            this.showToast((res && res.message) || '删除失败，请重试')
            return
          }
        } catch (e) {
          this.showToast('网络异常，请重试')
          return
        } finally {
          this.busy = false
        }
      }
      this.items = this.items.filter(x => x.id !== t.id)
      this.persist()
      this.deleteTarget = null
      this.showToast('已删除')
    },
    // 一次性清掉所有已完成的随想：后端批量接口逐条校验归属，越权整批回滚
    async clearCompleted() {
      const ids = this.items.filter(t => t.status === '已完成').map(t => t.id)
      if (ids.length === 0) return
      if (!this.isLoggedIn()) {
        const done = new Set(ids)
        this.items = this.items.filter(x => !done.has(x.id))
        this.persist()
        this.showToast(`已清理 ${ids.length} 条已完成`)
        return
      }
      this.clearing = true
      try {
        const res = await batchDeleteThoughts(ids)
        if (!(res && (res.success || res.code === 200))) {
          this.showToast((res && res.message) || '清理失败，请重试')
          return
        }
        const done = new Set(ids)
        this.items = this.items.filter(x => !done.has(x.id))
        this.showToast(`已清理 ${ids.length} 条已完成`)
      } catch (e) {
        this.showToast('网络异常，请重试')
      } finally {
        this.clearing = false
      }
    },
    // ---- 编辑随想 ----
    // 后端 PUT /thoughts/{id} 本就支持改内容/分类/优先级，这里把入口补上：
    // 之前只能删了重记，会丢创建时间。
    openEdit(id) {
      const t = this.items.find(x => x.id === id)
      if (!t) return
      this.scheduleTarget = null
      this.editTarget = id
      this.editForm = { content: t.content, detail: t.detail || '', type: t.type, priority: t.priority }
    },
    // 长备注展开/收起。Vue 2 给响应式对象新增 key 必须用 $set，
    // 直接 this.expanded[id] = v 会让它脱离响应式、模板不更新。
    isExpanded(id) {
      return !!this.expanded[id]
    },
    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id])
    },
    async saveEdit() {
      const t = this.items.find(x => x.id === this.editTarget)
      if (!t) return
      const content = this.editForm.content.trim()
      if (!content) {
        this.showToast('内容不能为空')
        return
      }
      if (this.isLoggedIn()) {
        this.busy = true
        try {
          const res = await updateThought(t.id, {
            content,
            detail: (this.editForm.detail || '').trim(),
            category: CATEGORY_CODE[this.editForm.type] || 'OTHER',
            priority: PRIORITY_CODE[this.editForm.priority] || 'MEDIUM'
          })
          if (res && (res.success || res.code === 200)) {
            Object.assign(t, this.fromVO(res.data))
          } else {
            this.showToast((res && res.message) || '保存失败，请重试')
            return
          }
        } catch (e) {
          this.showToast('网络异常，请重试')
          return
        } finally {
          this.busy = false
        }
      } else {
        t.content = content
        t.detail = (this.editForm.detail || '').trim()
        t.type = this.editForm.type
        t.priority = this.editForm.priority
        this.persist()
      }
      this.editTarget = null
      this.showToast('已更新 ✏️')
    },
    openSchedule(id) {
      this.scheduleTarget = id
      const t = this.items.find(x => x.id === id)
      this.scheduleForm = {
        date: t.date || this.today(),
        start: t.start || '09:00',
        end: t.end || '10:00',
        type: t.type || '工作'
      }
    },
    async saveSchedule() {
      const t = this.items.find(x => x.id === this.scheduleTarget)
      if (!t) return
      const { date, start, end, type } = this.scheduleForm
      const startTime = date ? `${date}T${start || '09:00'}:00` : null
      const endTime = date && end ? `${date}T${end}:00` : null
      if (this.isLoggedIn()) {
        this.busy = true
        try {
          const res = await scheduleThought(t.id, { startTime, endTime })
          if (res && (res.success || res.code === 200)) {
            Object.assign(t, this.fromVO(res.data))
          } else {
            this.showToast((res && res.message) || '安排失败，请重试')
            return
          }
          if (type && type !== t.type) {
            const res2 = await updateThought(t.id, { category: CATEGORY_CODE[type] || 'OTHER' })
            if (res2 && (res2.success || res2.code === 200)) {
              Object.assign(t, this.fromVO(res2.data))
            }
          }
        } catch (e) {
          this.showToast('网络异常，请重试')
          return
        } finally {
          this.busy = false
        }
      } else {
        t.date = date
        t.start = start
        t.end = end
        t.type = type
        t.plan = true
        if (t.status !== '已完成') t.status = '进行中'
        this.persist()
      }
      this.scheduleTarget = null
      this.showToast('已安排时间')
    },
    // ---- 随想 → 时间块 ----
    // 时间块必须挂在「工作区 + 工作表」下，随想只是用户级收集箱，
    // 所以转换时要在弹窗里补上归属，再调时间线接口建块。
    async openConvert(id) {
      if (!this.isLoggedIn()) {
        this.showToast('登录后才能转为时间块')
        return
      }
      const t = this.items.find(x => x.id === id)
      if (!t) return
      this.convertTarget = id
      this.convertForm = {
        projectId: this.convertForm.projectId || '',
        tagId: '',
        date: t.date || this.today(),
        start: t.start || '09:00',
        end: t.end || '10:00'
      }
      await this.ensureConvertOptions()
    },
    async ensureConvertOptions() {
      if (this.convertProjects.length) return
      this.convertLoading = true
      try {
        let wsId = localStorage.getItem('taskblock-current-workspace-id')
        if (!wsId) {
          const res = await listWorkspaces()
          const list = (res && res.data) || []
          if (!list.length) {
            this.showToast('还没有工作区，先去新建一个工作表')
            return
          }
          wsId = String(list[0].id)
          localStorage.setItem('taskblock-current-workspace-id', wsId)
        }
        this.workspaceId = wsId

        const projRes = await listProjects(wsId)
        if (!(projRes && (projRes.success || projRes.code === 200))) {
          this.showToast((projRes && projRes.message) || '工作表加载失败')
          return
        }
        const projects = (projRes.data || []).map(p => ({
          id: String(p.id),
          name: p.name || '未命名工作表'
        }))
        if (!projects.length) {
          this.showToast('当前工作区还没有工作表')
          return
        }
        this.convertProjects = projects
        // 上次选过的工作表还在就用它，否则默认第一个
        if (!projects.some(p => p.id === this.convertForm.projectId)) {
          this.convertForm.projectId = projects[0].id
        }
        await this.loadConvertTags()
      } catch (e) {
        this.showToast('网络异常，稍后重试')
      } finally {
        this.convertLoading = false
      }
    },
    async loadConvertTags() {
      const pid = this.convertForm.projectId
      if (!pid) {
        this.convertTags = []
        this.convertForm.tagId = ''
        return
      }
      try {
        const res = await listTagsByProject(pid)
        this.convertTags = (res && res.data) || []
      } catch (e) {
        this.convertTags = []
      }
      this.convertForm.tagId = ''
    },
    // 分类名带 fa:xxx 图标前缀时只取文字部分，和表格页口径一致
    tagName(tag) {
      const raw = String((tag && tag.name) || '')
      return raw.replace(/^fa:[^\s]*\s*/, '').trim() || raw
    },
    async confirmConvert() {
      const t = this.items.find(x => x.id === this.convertTarget)
      if (!t) return
      const { projectId, tagId, date, start, end } = this.convertForm
      if (!projectId) {
        this.showToast('请选择工作表')
        return
      }
      if (!date) {
        this.showToast('请选择日期')
        return
      }
      const plannedStartTime = `${date}T${start || '09:00'}:00`
      const plannedEndTime = `${date}T${end || '10:00'}:00`
      const tag = this.convertTags.find(x => String(x.id) === String(tagId))
      // 后端对颜色有 #RRGGBB 正则校验，脏数据直接不传，避免整单失败
      const color = tag && tag.color && /^#[A-Fa-f0-9]{6}$/.test(tag.color) ? tag.color : null

      this.convertSaving = true
      try {
        // 一次调用完成「建时间块 + 出箱」：后端单事务，不会出现建块成功、
        // 收集箱里却还留着同一条的情况；随想也不再软删，仍可通过 convertedBlockId 回溯。
        const res = await convertThought(t.id, {
          workspaceId: this.workspaceId,
          projectId,
          tagId: tagId || null,
          startTime: plannedStartTime,
          endTime: plannedEndTime,
          color
        })
        if (!(res && (res.success || res.code === 200))) {
          this.showToast((res && res.message) || '转换失败，请重试')
          return
        }
        // 后端已把随想置为 CONVERTED，列表不再返回它，这里同步移出本地
        this.items = this.items.filter(x => x.id !== t.id)
        this.convertTarget = null
        this.showToast('已转为时间块 🗓')
      } catch (e) {
        this.showToast('网络异常，请重试')
      } finally {
        this.convertSaving = false
      }
    },
    showToast(msg) {
      this.toastMsg = msg
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => { this.toastMsg = '' }, 2200)
    }
  }
}
</script>

<style scoped>
.thoughts-page {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Microsoft YaHei", sans-serif;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  gap: 0.938rem;
  overflow: hidden;
}

.thoughts-page * {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Microsoft YaHei", sans-serif;
}

/* 左右区域 */
.left,
.right {
  padding: 15px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.left {
  flex: 2;
  background: #edf4d8;
  border: 4px solid #57795c;
  border-radius: 18px;
  box-shadow: 0 6px 0 #426246, 0 12px 20px rgba(0, 0, 0, 0.15);
}

.right {
  flex: 8;
  background: #f5f1d5;
  border: 4px solid #698b52;
  border-radius: 0.5rem;
  box-shadow: 3px 3px 0 #587342;
}

/* 标题 */
.title {
  font-size: 22px;
  font-weight: bold;
  color: #263b29;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  flex-shrink: 0;
}

.icon {
  width: 32px;
  height: 32px;
  background: #f1dc7b;
  border: 3px solid #80672c;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.offline-tip {
  font-size: 12px;
  color: #8a6d3b;
  background: #fdf5d9;
  border: 1px dashed #c8b26a;
  border-radius: 10px;
  padding: 6px 10px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

/* ---------- 左侧新增任务 ---------- */
.add-card {
  background: #edf2d5;
  border-radius: 16px;
  border: 2px dashed #91a67b;
  box-shadow: 0 6px 16px -8px rgba(87, 121, 92, 0.25);
  padding: 16px;
  flex-shrink: 0;
}

.add-row {
  display: flex;
  gap: 10px;
}

.add-input {
  flex: 1;
  border: none;
  background: #f7f7df;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  color: #263b29;
  outline: none;
  transition: box-shadow 0.15s ease;
}

.add-input:focus {
  box-shadow: 0 0 0 3px rgba(145, 166, 123, 0.35);
}

.add-input::placeholder {
  color: #8a9a78;
}

.add-btn {
  border: none;
  background: #f1dc7b;
  color: #263b29;
  font-weight: 700;
  font-size: 14px;
  padding: 0 20px;
  border-radius: 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.12s ease, background 0.15s ease;
  border: 2px solid #987a2c;
}

.add-btn:hover {
  background: #e8d06a;
  transform: translateY(-1px);
}

.add-btn:active {
  transform: translateY(0);
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.add-meta {
  display: flex;
  gap: 14px;
  margin-top: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.meta-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.meta-label {
  font-size: 12px;
  color: #57795c;
  font-weight: 600;
}

.chip {
  border: 1.5px solid transparent;
  background: #f7f7df;
  color: #57795c;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.12s ease;
}

.chip.active.work { background: #ffe3da; color: #c45c3d; border-color: #c45c3d; }
.chip.active.life { background: #fff3ce; color: #a87400; border-color: #d4a017; }
.chip.active.study { background: #dff0ff; color: #2e7abf; border-color: #2e7abf; }
.chip.active.other { background: #dbf7ea; color: #2a8f5c; border-color: #2a8f5c; }
.chip.active.hi { background: #ffe0e7; color: #c43d55; border-color: #c43d55; }
.chip.active.mid { background: #ffebd3; color: #b36a00; border-color: #b36a00; }
.chip.active.low { background: #dff5e8; color: #2a8f5c; border-color: #2a8f5c; }

/* ---------- 右侧筛选标签 ---------- */
.tabs {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 14px;
  flex-shrink: 0;
}

.tab {
  border: none;
  background: #edf2d5;
  color: #57795c;
  font-weight: 700;
  font-size: 13px;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 2px 6px -3px rgba(38, 59, 41, 0.15);
  border: 2px solid #91a67b;
  transition: all 0.15s ease;
}

.tab.active {
  background: #f1dc7b;
  color: #263b29;
  border-color: #987a2c;
  box-shadow: 0 4px 10px -4px rgba(152, 122, 44, 0.35);
}

.tab .count {
  font-size: 11px;
  margin-left: 4px;
  opacity: 0.85;
}

/* ---------- 卡片网格 ---------- */
.grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 14px 4px 14px 0;
  align-content: start;
}

.card {
  position: relative;
  background: #edf2d5;
  border-radius: 14px;
  padding: 16px 14px 13px;
  box-shadow: 0 6px 16px -8px rgba(87, 121, 92, 0.22);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  border: 2px solid #91a67b;
}

.card::before {
  content: "";
  position: absolute;
  top: -7px;
  left: 22px;
  width: 38px;
  height: 14px;
  background: rgba(241, 220, 123, 0.55);
  border-radius: 3px;
  transform: rotate(-4deg);
  pointer-events: none;
}

.card:hover {
  transform: rotate(0deg) translateY(-3px) !important;
  box-shadow: 0 10px 24px -10px rgba(87, 121, 92, 0.3);
}

.card.done {
  opacity: 0.7;
}

.card-top {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.checkbox {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 7px;
  border: 2px solid #91a67b;
  margin-top: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #fff;
  transition: all 0.15s ease;
  background: #f5f5dc;
}

.checkbox.checked {
  background: #8dbb6b;
  border-color: #6a9b4f;
}

.card-text {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: #263b29;
  flex: 1;
  word-break: break-word;
}

.card.done .card-text {
  text-decoration: line-through;
  color: #57795c;
}

.tag-row {
  display: flex;
  gap: 8px;
  margin: 10px 0 4px;
  flex-wrap: wrap;
}

.tag {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
}

.tag.work { background: #ffe3da; color: #c45c3d; }
.tag.life { background: #fff3ce; color: #a87400; }
.tag.study { background: #dff0ff; color: #2e7abf; }
.tag.other { background: #dbf7ea; color: #2a8f5c; }
.tag.hi { background: #ffe0e7; color: #c43d55; }
.tag.mid { background: #ffebd3; color: #b36a00; }
.tag.low { background: #dff5e8; color: #2a8f5c; }

.schedule-line {
  margin-top: 10px;
  font-size: 12px;
  color: #57795c;
  background: #f7f7df;
  border-radius: 10px;
  padding: 7px 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.card-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #b8c9a2;
}

.action-row {
  display: flex;
  gap: 8px;
}

/* 卡片主操作：整行实心主色，和下面一排次要操作拉开层级 */
.action-btn.convert-btn {
  background: var(--lp-primary, #4aa46a);
  border: 2px solid var(--lp-primary, #4aa46a);
  color: #fff;
}

.action-btn.convert-btn:hover {
  background: var(--lp-primary-dark, #2f7a4a);
  color: #fff;
}

.action-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.action-btn {
  flex: 1;
  border: none;
  background: #f7f7df;
  color: #57795c;
  font-weight: 700;
  font-size: 12px;
  padding: 7px 0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn:hover {
  background: #f1dc7b;
  color: #263b29;
}

.action-btn.del {
  color: #c45c5c;
  background: #fde8e8;
}

.action-btn.del:hover {
  background: #c45c5c;
  color: #fff;
}

.empty {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  color: #57795c;
}

.empty .em-face {
  font-size: 24px;
  color: #f1dc7b;
  margin-bottom: 8px;
  text-shadow: 1px 1px 0 #987a2c;
}

/* ---------- 弹窗 ---------- */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(38, 59, 41, 0.35);
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 20px;
}

.overlay.open {
  display: flex;
}

.modal {
  width: 100%;
  max-width: 360px;
  background: #f5f5dc;
  border-radius: 16px;
  padding: 22px 20px 18px;
  box-shadow: 0 16px 32px -12px rgba(38, 59, 41, 0.45);
  position: relative;
  border: 3px solid #71866a;
}

.modal::before {
  content: "";
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%) rotate(-3deg);
  width: 48px;
  height: 14px;
  background: rgba(241, 220, 123, 0.5);
  border-radius: 3px;
}

.modal h3 {
  font-size: 20px;
  font-weight: 700;
  color: #263b29;
  margin: 4px 0 16px;
  text-align: center;
}

.modal-tip {
  font-size: 12px;
  line-height: 1.6;
  color: var(--lp-text-3, #94a59b);
  margin: -8px 0 14px;
  text-align: center;
}

.field {
  margin-bottom: 12px;
}

.field label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #57795c;
  margin-bottom: 5px;
}

.field input,
.field select {
  width: 100%;
  border: 1.5px solid #91a67b;
  background: #f7f7df;
  border-radius: 10px;
  padding: 9px 11px;
  font-size: 13px;
  color: #263b29;
  outline: none;
}

.field input:focus,
.field select:focus {
  border-color: #57795c;
  box-shadow: 0 0 0 2px rgba(145, 166, 123, 0.25);
}

.time-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.time-row span {
  color: #57795c;
  font-size: 12px;
}

.modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.modal-actions button {
  flex: 1;
  border: none;
  padding: 10px 0;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.modal-actions .cancel {
  background: #f7f7df;
  color: #57795c;
}

.modal-actions .save {
  background: #f1dc7b;
  color: #263b29;
  border: 2px solid #987a2c;
}

.modal-actions .save:hover {
  background: #e8d06a;
}

/* ---------- Toast ---------- */
.toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(20px);
  background: #57795c;
  color: #fff;
  padding: 10px 20px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  opacity: 0;
  pointer-events: none;
  transition: all 0.25s ease;
  z-index: 60;
}

.toast.show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

@media (max-width: 900px) {
  .thoughts-page {
    flex-direction: column;
    overflow: auto;
  }
  .left,
  .right {
    flex: none;
    height: auto;
    overflow: visible;
  }
  .grid {
    overflow: visible;
  }
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，尺寸、间距、字号、布局一律沿用上面的原值。
   分类 / 优先级标签色（.chip.active.* 与 .tag.*）是语义数据色，保持原样不动。 */
/* 左右两个大板块：米黄厚涂 + 硬阴影 → 白底 + 块级重描边 + 柔阴影 */
.left{
  background:var(--lp-surface,#fff);
  border:3px solid var(--lp-border-strong,#a5cdb3);
  border-radius:1rem;
  box-shadow:var(--lp-shadow,0 2px 10px rgba(52,102,68,.06));
}
.right{
  background:var(--lp-surface,#fff);
  border:3px solid var(--lp-border-strong,#a5cdb3);
  border-radius:1rem;
  box-shadow:var(--lp-shadow,0 2px 10px rgba(52,102,68,.06));
}
/* 标题 / 图标徽章 */
.title{
  color:var(--lp-text,#26332b);
}
.icon{
  background:var(--lp-primary-soft,#e9f5ec);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
}
/* 新增任务卡片 */
.add-card{
  background:var(--lp-surface-2,#f7fbf8);
  border:2px dashed var(--lp-border,#e3efe6);
  border-radius:0.875rem;
  box-shadow:none;
}
/* 输入框 */
.add-input{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text,#26332b);
}
.add-input:focus{
  border-color:var(--lp-primary,#4aa46a);
  box-shadow:none;
}
.add-input::placeholder{
  color:var(--lp-text-3,#94a59b);
}
/* 主按钮（实心） */
.add-btn{
  background:var(--lp-primary,#4aa46a);
  border:2px solid var(--lp-primary,#4aa46a);
  border-radius:0.5rem;
  color:#fff;
}
.add-btn:hover{
  background:var(--lp-primary-dark,#2f7a4a);
}
/* 分类 / 优先级 chip（仅默认态，激活态是数据色） */
.meta-label{
  color:var(--lp-text-2,#5f7268);
}
.chip{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text-2,#5f7268);
}
/* 筛选标签 */
.tab{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text-2,#5f7268);
  box-shadow:none;
}
.tab.active{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary,#4aa46a);
  color:var(--lp-primary-dark,#2f7a4a);
  box-shadow:none;
}
/* 卡片 */
.card{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.75rem;
  box-shadow:var(--lp-shadow,0 2px 10px rgba(52,102,68,.06));
}
/* 胶带装饰：亮黄半透明 → 主色半透明 */
.card::before{
  background:rgba(74,164,106,.15);
}
.card:hover{
  box-shadow:var(--lp-shadow-lg,0 8px 24px rgba(52,102,68,.08));
}
.card-text{
  color:var(--lp-text,#26332b);
}
.card.done .card-text{
  color:var(--lp-text-3,#94a59b);
}
/* 勾选框 */
.checkbox{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
}
.checkbox.checked{
  background:var(--lp-primary,#4aa46a);
  border-color:var(--lp-primary-dark,#2f7a4a);
}
/* 日程条 */
.schedule-line{
  background:var(--lp-surface-2,#f7fbf8);
  border-radius:0.5rem;
  color:var(--lp-text-2,#5f7268);
}
/* 卡片底部操作 */
.card-actions{
  border-top:1px dashed var(--lp-border-soft,#eef5f0);
}
.action-btn{
  background:var(--lp-surface-2,#f7fbf8);
  border-radius:0.5rem;
  color:var(--lp-text-2,#5f7268);
}
.action-btn:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.action-btn.del{
  background:#fdecec;
  color:#d9534f;
}
.action-btn.del:hover{
  background:#d9534f;
  color:#fff;
}
/* 空态 */
.empty{
  color:var(--lp-text-3,#94a59b);
}
.empty .em-face{
  color:var(--lp-text-3,#94a59b);
  text-shadow:none;
}
/* 弹窗外壳 */
.overlay{
  background:rgba(38,51,43,.35);
}
.modal{
  background:var(--lp-surface,#fff);
  border:1px solid var(--lp-border,#e3efe6);      /* 2026-10-04：去掉 3px 绿系描边，改极淡中性描边 */
  border-radius:1rem;
  box-shadow:var(--lp-shadow-lg,0 8px 24px rgba(52,102,68,.08));
}
.modal::before{
  background:rgba(74,164,106,.15);
}
.modal h3{
  color:var(--lp-text,#26332b);
}
/* 弹窗表单 */
.field label{
  color:var(--lp-text-2,#5f7268);
}
.field input,
.field select{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text,#26332b);
}
.field input:focus,
.field select:focus{
  border-color:var(--lp-primary,#4aa46a);
  box-shadow:none;
}
.time-row span{
  color:var(--lp-text-2,#5f7268);
}
/* 弹窗底部按钮区：取消=描边、保存=实心主色 */
.modal-actions .cancel{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text-2,#5f7268);
}
.modal-actions .save{
  background:var(--lp-primary,#4aa46a);
  border:2px solid var(--lp-primary,#4aa46a);
  border-radius:0.5rem;
  color:#fff;
}
.modal-actions .save:hover{
  background:var(--lp-primary-dark,#2f7a4a);
}
/* Toast */
.toast{
  background:var(--lp-primary-dark,#2f7a4a);
}

/* ===== 记录随想板块 · 可读性 + 自适应优化 =====
   解决三个问题：
     ① 字号偏小（chip / meta-label 12px、input 14px）→ 整体上调；
     ② 分类 / 优先级 chip 因 flex-wrap 断成「孤行」，排版散乱 → 标签独占一行 + 等宽平分；
     ③ 左栏 flex:2 在视口变窄 / 浏览器缩放时被挤扁，input 的 min-width:auto
        顶住不肯收缩，把「记下来」按钮挤出卡片外（被 overflow:hidden 裁掉）→ 左栏给定
        稳定宽度 + 输入区改纵向堆叠，任何宽度都不再互相挤压。
   仅作用于左侧「记录随想」；右侧「事件展示」与分类/优先级语义色保持原样。 */

/* —— 1. 左栏给定稳定宽度（24% 自适应，夹在 300~360px），右栏吃掉剩余空间 —— */
.left{
  flex:0 0 clamp(300px, 24%, 360px);
  min-width:0;
}
.right{
  flex:1 1 0;
  min-width:0;
}

/* —— 2. 输入区：改为纵向堆叠，输入框与按钮各占整行，永不互相挤压 —— */
.add-card{
  padding:18px;
}
.add-row{
  flex-direction:column;
  gap:10px;
}
.add-input{
  flex:0 0 auto;
  width:100%;
  min-width:0;
  font-size:15px;
  line-height:1.5;
  padding:11px 14px;
}
.add-btn{
  flex:0 0 auto;
  width:100%;
  min-height:48px;
  font-size:15px;
  letter-spacing:.04em;
}
/* 禁用态：原样式用 opacity:.5，白字叠在浅绿底上几乎看不清 →
   改为「浅绿底 + 深绿字」，既明确不可点，又保证可读性 */
.add-btn:disabled{
  opacity:1;
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-border,#e3efe6);
  color:var(--lp-primary-dark,#2f7a4a);
  cursor:not-allowed;
  transform:none;
}

/* —— 3. 分类 / 优先级：标签独占一行，选项等宽平分整行，不再出现孤行 —— */
.add-meta{
  flex-direction:column;
  align-items:stretch;
  gap:14px;
  margin-top:16px;
}
.meta-group{
  flex-direction:column;
  align-items:stretch;
  gap:8px;
}
.meta-label{
  font-size:13px;
  font-weight:700;
  letter-spacing:.02em;
}
.chip-row{
  display:grid;
  grid-auto-flow:column;
  grid-auto-columns:minmax(0,1fr);
  gap:8px;
}
.chip{
  width:100%;
  min-width:0;
  overflow:hidden;
  text-align:center;
  white-space:nowrap;
  text-overflow:ellipsis;
  font-size:13px;
  padding:8px 6px;
}
/* 未选中态 hover 反馈（选中态 .chip.active.* 权重更高，不受影响） */
.chip:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary,#4aa46a);
  color:var(--lp-primary-dark,#2f7a4a);
}

/* —— 4. 窄屏改上下堆叠时，重申布局（本块在媒体查询之后，必须重新声明才生效） —— */
@media (max-width:900px){
  .left,
  .right{
    flex:none;
    width:100%;
  }
  .add-meta{
    flex-direction:row;
    flex-wrap:wrap;
    gap:14px 24px;
  }
  .meta-group{
    flex:1 1 240px;
  }
}

/* ===== 统计回顾条 ===== */
.stats-line{
  display:flex;
  align-items:center;
  gap:6px;
  flex-wrap:wrap;
  font-size:12px;
  color:var(--lp-text-3,#94a59b);
  margin-bottom:10px;
  flex-shrink:0;
}
.stats-line b{
  font-weight:500;
  color:var(--lp-text-2,#5f7268);
}
.stats-line .dot{
  opacity:.6;
}
.stats-line .warn,
.stats-line .warn b{
  color:#d9534f;
}

/* ===== 卡片长备注 ===== */
.card-detail{
  margin-top:8px;
}
.detail-text{
  font-size:12px;
  line-height:1.6;
  color:var(--lp-text-2,#5f7268);
  white-space:pre-wrap;
  word-break:break-word;
  background:var(--lp-surface-2,#f7fbf8);
  border-radius:0.5rem;
  padding:8px 10px;
  display:-webkit-box;
  -webkit-line-clamp:2;
  -webkit-box-orient:vertical;
  overflow:hidden;
}
.detail-text.expanded{
  display:block;
  overflow:visible;
}
.detail-toggle{
  margin-top:4px;
  border:none;
  background:none;
  padding:0;
  font-size:11px;
  font-weight:500;
  color:var(--lp-primary-dark,#2f7a4a);
  cursor:pointer;
}
.detail-toggle:hover{
  text-decoration:underline;
}
.detail-input{
  width:100%;
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text,#26332b);
  font-size:13px;
  font-family:inherit;
  line-height:1.6;
  padding:9px 11px;
  outline:none;
  resize:vertical;
}
.detail-input:focus{
  border-color:var(--lp-primary,#4aa46a);
}

/* ===== 搜索框 ===== */
.search-row{
  position:relative;
  margin-bottom:12px;
  flex-shrink:0;
}
.search-input{
  width:100%;
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text,#26332b);
  font-size:13px;
  padding:9px 34px 9px 12px;
  outline:none;
}
.search-input:focus{
  border-color:var(--lp-primary,#4aa46a);
}
.search-input::placeholder{
  color:var(--lp-text-3,#94a59b);
}
.search-clear{
  position:absolute;
  top:50%;
  right:8px;
  transform:translateY(-50%);
  width:20px;
  height:20px;
  border:none;
  border-radius:50%;
  background:var(--lp-surface-2,#f7fbf8);
  color:var(--lp-text-2,#5f7268);
  font-size:14px;
  line-height:1;
  cursor:pointer;
}
/* 清空已完成：红色虚线，和筛选 tab、排序按钮区分开 */
.tab.clear-btn{
  border-style:dashed;
  border-color:#d9534f;
  color:#d9534f;
}
.tab.clear-btn:hover{
  background:#fdecec;
}
.tab.clear-btn:disabled{
  opacity:.55;
  cursor:not-allowed;
}
/* 删除确认弹窗里引用一遍待删内容，避免删错对象 */
.confirm-quote{
  background:var(--lp-surface-2,#f7fbf8);
  border-radius:0.5rem;
  padding:10px 12px;
  font-size:13px;
  line-height:1.6;
  color:var(--lp-text,#26332b);
  word-break:break-word;
  margin-bottom:4px;
}
.modal-actions .save.danger{
  background:#d9534f;
  border:2px solid #d9534f;
  color:#fff;
}
.modal-actions .save.danger:hover{
  background:#c9302c;
}

/* ===== 排序切换 + 今天/逾期角标 ===== */
/* 排序按钮复用 .tab 的外观，但用虚线描边降一档，避免被误认为是一个筛选项 */
.tab.sort-toggle{
  border-style:dashed;
  font-weight:500;
}
.tab.sort-toggle.active{
  background:var(--lp-primary-soft,#e9f5ec);
  border-style:solid;
  border-color:var(--lp-primary,#4aa46a);
  color:var(--lp-primary-dark,#2f7a4a);
}
/* 日程条：今天=主色浅底，逾期=红底 */
.schedule-line.is-today{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.schedule-line.is-overdue{
  background:#fdecec;
  color:#d9534f;
}
.badge{
  flex-shrink:0;
  font-size:11px;
  font-weight:700;
  padding:2px 8px;
  border-radius:999px;
  letter-spacing:.02em;
}
.badge-today{
  background:var(--lp-primary,#4aa46a);
  color:#fff;
}
.badge-overdue{
  background:#d9534f;
  color:#fff;
}
</style>
